import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { APP_CONFIG } from '@src/app/app-config.module';
import { ApplicationSegment, ComplianceStatus, WebappInitializationData } from '../models/data-model';
import { BundleInfoService } from './bundle-info.service';
import { RegistrationWebappPushApiService } from './registration-webapp-push-api.service';
import { SoldoProductMarket } from './registration.service';
import { SoldoCookieService } from './soldo-cookie.service';

const GTM_EVENT_CALLBACK_TIMEOUT = 2000;

@Injectable()
export class GoogleTagManagerService {

    private readonly eventsCategory: string = "Registration";
    private clientId: string;
    private externalId: string;
    private productMarket: SoldoProductMarket;
    private countryOfIncorporation: string;
    private companySize: number;
    private companyType: string;
    clientIdPromise: Promise<string>;

    constructor(
        private readonly pushAPI: RegistrationWebappPushApiService,
        private readonly bundleInfoService: BundleInfoService,
        private readonly cookieService: SoldoCookieService
    ) {
        const gtmWorkspaceId = inject(APP_CONFIG).googleTagManagerID;
        if (gtmWorkspaceId) {
            window['sldLoadGtmScript'](gtmWorkspaceId);
        }
    }

    init(): Promise<void> {
        return this.requestGoogleClientID().then(clientId => {
            this.clientId = clientId;
        });
    }

    setExternalId(extId: string) {
        this.externalId = extId;
        this.cookieService.storeGTMExternalId(extId);
    }

    setProductMarket(market: SoldoProductMarket) {
        this.productMarket = market;
    }

    setCountryOfIncorporation(country: string) {
        this.countryOfIncorporation = country;
    }

    setCompanySize(companySize: number) {
        this.companySize = companySize;
    }

    setCompanyType(companyType: string) {
        this.companyType = companyType;
    }

    onNavigationEnd(routeSnapshot: ActivatedRouteSnapshot) {
        const pageEventParams: object = this.getDataLayerVariables();
        pageEventParams["url"] = location.href;
        pageEventParams["path"] = location.hash.replace('#', '');
        pageEventParams["category"] = this.eventsCategory;
        pageEventParams["title"] = routeSnapshot.data['pageName'] || "untitled";
        pageEventParams["event"] = "virtual_page_view";

        this.addToDataLayer(pageEventParams);
    }

    trackEmailVerified() {
        this.addToDataLayer({
            event: "Email Verified"
        });
    }

    trackRegistrationWebappInit(initData: WebappInitializationData) {
        this.addToDataLayer({
            event: "Registration Webapp Init",
            label: `${initData.planName}-${initData.offerName}`
        });
        this.addToDataLayer({
            event: "Signup Started",
            label: `${initData.planName}-${initData.offerName}`
        });
    }

    trackNewRegistrationStarted(planName: string, offerName: string, segment: ApplicationSegment): void {
        this.addToDataLayer({
            event: "New Registration Started",
            label: `${planName}-${offerName}`,
            segment: segment
        });
    }

    trackSignedUp(): void {
        this.addToDataLayer({
            event: "Signup Completed",
            label: [this.bundleInfoService.getCurrentApplicationPlan(), this.bundleInfoService.getCurrentApplicationOffer()].join('-')
        });
    }

    trackRegistrationResumed(): void {
        this.addToDataLayer({
            event: "Registration Resumed"
        });
    }

    trackRegistrationCompleted(eKYCPassed: ComplianceStatus): void {
        this.addToDataLayer({
            event: "Registration Completed",
            label: "Business KYB Form",
            value: "1",
            eKYCStatus: eKYCPassed == ComplianceStatus.COMPANY_PROVISIONED ? "pass" : "fail"
        });
    }

    trackStartOver(callback?: (...args: unknown[]) => void): void {
        this.addToDataLayer({
            event: "Start Over Next Registration"
        }, callback);
    }

    trackEKYCPassed(): void {
        this.addToDataLayer({
            event: "eKYC Passed"
        });
    }

    trackSubStepViewed(subStepName: string) {
        this.addToDataLayer({
            event: "SubStep Viewed",
            subStepName: subStepName
        });
    }

    trackSubStepCompleted(subStepName: string) {
        this.addToDataLayer({
            event: "SubStep Completed",
            subStepName: subStepName
        });
    }

    private getDataLayerVariables(): object {
        const variables: object = {
            category: this.eventsCategory,
            uid: this.externalId,
            product: this.bundleInfoService.getCurrentApplicationProductName(),
            plan: this.bundleInfoService.getCurrentApplicationPlan(),
            offer: this.bundleInfoService.getCurrentApplicationOffer(),
            location: document.location.href || "undefinedValue",
            referrer: document.referrer || "undefinedValue"
        };

        if (this.clientId) {
            variables["cid"] = btoa(this.clientId);
        }

        if (this.productMarket) {
            variables["market"] = this.productMarket;
        }
        if (this.countryOfIncorporation) {
            variables["companyCountry"] = this.countryOfIncorporation;
        }
        if (this.companySize) {
            variables["companySize"] = this.companySize;
        }
        if (this.companyType) {
            variables["companyType"] = this.companyType;
        }

        return variables;
    }

    private addToDataLayer(data, eventCallback?: (...args: unknown[]) => void) {
        if (typeof eventCallback == "function") {
            let callbackInvoked = false;
            const calledOnce = () => {
                if (!callbackInvoked) {
                    eventCallback();
                    callbackInvoked = true;
                }
            };
            data["eventCallback"] = calledOnce;
            setTimeout(calledOnce, GTM_EVENT_CALLBACK_TIMEOUT);
        }
        this.pushAPI.promise('gtm-consent-ready').then(() => {
            window["dataLayer"].push(Object.assign(this.getDataLayerVariables(), data));
        });
    }

    requestGoogleClientID(): Promise<string> {
        return this.pushAPI.promise("ga-client-id").then(e => {
            return e.value as string;
        });
    }

}
